import * as React from 'react';
import { MutableRefObject, ReactElement, useCallback, useState } from 'react';
import { SearchDateTerms } from '../../types/common/searchfilter';
import { CLOSETGroupsManager, CLOSETGroupsSearchType } from '../../types/api/groups/closet';
import {
  initialGroupsSearchFilter,
  paymentMethodOptions,
  planTypeOptions,
} from '../../constants/groups/filter';
import {
  clampKeyword,
  searchTypeConfig,
  searchTypeOptions,
} from '../../constants/groups/searchType';
import { useCLOSETGroupsManagersQuery } from 'hooks/groups/useCLOSETGroupsManagersQuery';
import { GroupsRef } from 'components/groups/GroupList';
import { CLOSETGroupsInitialSearchParams } from '../../hooks/groups/useCLOSETGroupsInitialSearchParams';
import FilterLayout from '../common/searchfilter/FilterLayout';
import DateRangeFilter from '../common/searchfilter/contents/DateRangeFilter';
import CheckboxFilterElement from '../common/searchfilter/contents/CheckboxFilterElement';
import KeywordInput from '../common/searchfilter/contents/KeywordInput';
import FilterElementLayout from '../common/searchfilter/FilterElementLayout';
import AutocompleteInput from '../common/searchfilter/AutocompleteInput';
import Select from '../common/form/Select';
import { SelectChangeEvent } from '../../types/common/select';
import keyboardKey from 'keyboard-key';
import styled from 'styled-components';

interface Props {
  groupsRef?: MutableRefObject<GroupsRef | null>;
  initialSearchParams: CLOSETGroupsInitialSearchParams;
}

const SearchFilter = ({ groupsRef, initialSearchParams }: Props): ReactElement => {
  const [dateInterval, setDateInterval] = useState<string[]>(initialSearchParams.dateInterval);
  const [dateTerm, setDateTerm] = useState<SearchDateTerms | null>(initialSearchParams.dateTerm);
  const [planTypeValues, setPlanTypeValues] = useState<number[]>(
    initialSearchParams.planTypeValues,
  );
  const [paymentMethodValues, setPaymentMethodValues] = useState<number[]>(
    initialSearchParams.paymentMethodValues,
  );
  const [keyword, setKeyword] = useState<string>(initialSearchParams.keyword);
  const [searchType, setSearchType] = useState<CLOSETGroupsSearchType>(
    initialSearchParams.searchType,
  );

  const [manager, setManager] = useState<CLOSETGroupsManager | null>(
    initialGroupsSearchFilter.manager,
  );

  const managerOptions = useCLOSETGroupsManagersQuery({
    managerEmail: initialSearchParams.managerEmail,
    setManager,
  });

  const handleSearch = useCallback(
    (states: {
      dateInterval: string[];
      dateTerm: SearchDateTerms | null;
      planTypeValues: number[];
      paymentMethodValues: number[];
      keyword: string;
      searchType: CLOSETGroupsSearchType;
      manager: CLOSETGroupsManager | null;
    }): Promise<void> | void => {
      if (groupsRef?.current?.fetchGroupList === null) return;
      const { dateInterval, planTypeValues, paymentMethodValues, keyword, searchType, manager } =
        states;
      const [startDate, endDate] = dateInterval;
      return groupsRef?.current?.fetchGroupList(
        {
          startDate,
          endDate,
          planTypes: planTypeValues,
          paymentMethods: paymentMethodValues,
          keyword,
          searchType,
          managerEmail: manager?.email || '',
        },
        1,
      );
    },
    [groupsRef],
  );

  const setSearchParams = useCallback((): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('dateInterval', dateInterval.join(','));
    url.searchParams.set('planTypeValues', planTypeValues.join(','));
    url.searchParams.set('paymentMethodValues', paymentMethodValues.join(','));
    url.searchParams.set('keyword', keyword);
    if (dateTerm) {
      url.searchParams.set('dateTerm', dateTerm.toString());
    }
    url.searchParams.set('searchType', searchType.toString());
    if (manager) {
      url.searchParams.set('manager', manager.email);
    }
    window.history.replaceState(null, '', url.toString());
  }, [dateInterval, dateTerm, planTypeValues, paymentMethodValues, keyword, searchType, manager]);

  const deleteSearchParams = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('dateInterval');
    url.searchParams.delete('planTypeValues');
    url.searchParams.delete('paymentMethodValues');
    url.searchParams.delete('keyword');
    url.searchParams.delete('searchType');
    url.searchParams.delete('dateTerm');
    url.searchParams.delete('manager');
    window.history.replaceState(null, '', url.toString());
  };

  const onClickSearch = useCallback((): Promise<void> | void => {
    setSearchParams();
    return handleSearch({
      dateInterval,
      dateTerm,
      planTypeValues,
      paymentMethodValues,
      keyword,
      searchType,
      manager,
    });
  }, [
    dateInterval,
    dateTerm,
    planTypeValues,
    paymentMethodValues,
    keyword,
    searchType,
    manager,
    handleSearch,
  ]);

  const onClickReset = async (): Promise<void> => {
    setDateInterval(initialGroupsSearchFilter.dateInterval);
    setDateTerm(initialGroupsSearchFilter.dateTerm);
    setKeyword(initialGroupsSearchFilter.keyword);
    setSearchType(initialGroupsSearchFilter.searchType);
    setPlanTypeValues(initialGroupsSearchFilter.planTypeValues);
    setPaymentMethodValues(initialGroupsSearchFilter.paymentMethodValues);
    setManager(initialGroupsSearchFilter.manager);
    await handleSearch(initialGroupsSearchFilter);
    return deleteSearchParams();
  };

  const onChangeSearchType = useCallback((e: SelectChangeEvent): void => {
    const value = e.target.value as CLOSETGroupsSearchType;
    setSearchType(value);
    // The input's maxLength only blocks new typing, so trim what came from a wider type.
    setKeyword(previousKeyword => clampKeyword(previousKeyword, value));
  }, []);

  const onKeyDownKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputKey = keyboardKey.getCode(e);
    if (inputKey !== keyboardKey.Enter) {
      return;
    }
    return onClickSearch();
  };

  const onResetManager = () => {
    setManager(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('manager');
    return window.history.replaceState(null, '', url.toString());
  };

  return (
    <FilterLayout getSearchResult={onClickSearch} resetFilter={onClickReset}>
      <DateRangeFilter
        label={'Subscription Term Date'}
        dateInterval={dateInterval}
        setDateInterval={setDateInterval}
        selectedShortcut={dateTerm}
        setSelectedShortcut={setDateTerm}
      />
      <CheckboxFilterElement
        label={'Plan Type'}
        values={planTypeValues}
        options={planTypeOptions}
        onChange={setPlanTypeValues}
      />
      <CheckboxFilterElement
        label={'Payment Method'}
        values={paymentMethodValues}
        options={paymentMethodOptions}
        onChange={setPaymentMethodValues}
      />
      <FilterElementLayout label={'CLO-SET Manager'} gridSize={1}>
        <AutocompleteInput<CLOSETGroupsManager>
          value={manager}
          onChange={setManager}
          options={managerOptions.data}
          getOptionLabel={managerOptions.getManagerLabel}
          onReset={onResetManager}
        />
      </FilterElementLayout>
      <FilterElementLayout label={'Keyword'} gridSize={1}>
        <KeywordSearchBlock>
          <Select
            value={searchType}
            options={searchTypeOptions}
            variant={'outlined'}
            onChange={onChangeSearchType}
            width={'150px'}
          />
          <KeywordInput
            keyword={keyword}
            onChangeKeyword={e => setKeyword(e.target.value)}
            onKeyDownKeyword={onKeyDownKeyword}
            placeholder={searchTypeConfig[searchType].placeholder}
            maxLength={searchTypeConfig[searchType].maxLength}
            width={'100%'}
          />
        </KeywordSearchBlock>
      </FilterElementLayout>
    </FilterLayout>
  );
};

export default SearchFilter;

// [EN] The selector belongs in the field area, not the label slot — a selector in
//      the label slot leaves this row without its "Keyword" label and breaks the
//      left column every other filter row aligns to.
// [KO] 셀렉터는 label 슬롯이 아니라 입력 영역에 둔다. label 슬롯에 넣으면 이 행만
//      "Keyword" 라벨이 사라져 다른 필터 행들이 맞춰 선 왼쪽 열이 깨진다.
const KeywordSearchBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${props => props.theme.spacing(3)}px;

  /* [EN] Keeps the width the keyword input had on its own, so adding the selector
          redistributes space instead of widening the whole filter panel.
     [KO] 기존에 키워드 입력 혼자 쓰던 폭을 유지한다. 그래야 셀렉터가 추가돼도 필터
          패널 전체가 넓어지지 않고 공간을 나눠 쓰는 선에서 끝난다. */
  width: 50%;

  /* [EN] The selector holds its width; the input absorbs the remainder.
     [KO] 셀렉터는 폭을 지키고, 남는 자리는 입력이 흡수한다. */
  > *:first-child {
    flex-shrink: 0;
  }
`;
